import { useTranslations } from 'next-intl';
import { forwardRef } from 'react';

import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import ArrowDownIcon from '@flipster/react/icon/generated/ArrowDownIcon';
import ArrowUpIcon from '@flipster/react/icon/generated/ArrowUpIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

interface OrderbookCenterPriceProps {
  centerPrice: string;
  markPrice?: string; // spot doesn't have mark price
  priceDirection: 'up' | 'down';
  showRecenter: boolean;
  onRecenter: () => void;
  onClickCenterPrice: () => void;
  precision?: number;
}

export const OrderbookCenterPrice = forwardRef<HTMLDivElement, OrderbookCenterPriceProps>(
  ({ centerPrice, markPrice, priceDirection, showRecenter, onRecenter, onClickCenterPrice, precision }, ref) => {
    const t = useTranslations();
    const transTrade = useTranslations('Trade.r251104');
    return (
      <div
        ref={ref}
        className={hstack({
          py: 12,
          h: 50,
          gap: 2,
          position: 'sticky',
          top: -16,
          bottom: -16,
          bg: 'background.default.level-0',
          zIndex: 2,
          justifyContent: 'space-between',
        })}
      >
        <div className={hstack({ gap: 6 })}>
          <button className={hstack({ gap: 2 })} onClick={onClickCenterPrice}>
            <Formatted.Number
              value={centerPrice}
              precision={precision}
              method='round-half-up'
              toFixed
              css={{
                textStyle: 'Title/S/Monospace',
                color: priceDirection === 'up' ? 'content.accent.buy' : 'content.state.negative',
              }}
            />
            {priceDirection === 'up' ? (
              <ArrowUpIcon size={20} color={token('colors.content.accent.buy')} />
            ) : (
              <ArrowDownIcon size={20} color={token('colors.content.state.negative')} />
            )}
          </button>
          {markPrice && (
            <Tooltip placement='right' shape='gray'>
              <Tooltip.Trigger>
                <Formatted.Number
                  value={markPrice}
                  precision={precision}
                  method='round-half-up'
                  toFixed
                  css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' }}
                />
              </Tooltip.Trigger>
              <Tooltip.Content portal>
                <div className={css({ whiteSpace: 'nowrap' })}>{transTrade('The_current_mark_price')}</div>
              </Tooltip.Content>
            </Tooltip>
          )}
        </div>
        {showRecenter && (
          <TextButton
            size='small'
            onClick={onRecenter}
            label={t('Autogen_common.r251126.s_Recenter')}
            color='neutral'
          />
        )}
      </div>
    );
  },
);

OrderbookCenterPrice.displayName = 'OrderbookCenterPrice';
